import { Component, OnInit, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

interface DocumentationSection {
  id: string;
  title: string;
  icon: string;
  route: string;
  description: string;
  features: string[];
}

@Component({
  selector: 'app-documentation',
  imports: [CommonModule, RouterLink],
  templateUrl: './documentation.component.html',
  styleUrls: ['./documentation.component.css'],
  standalone: true
})
export class DocumentationComponent implements OnInit, AfterViewInit {
  currentTheme: 'light' | 'dark' = 'light';

  sections: DocumentationSection[] = [
    {
      id: 'home',
      title: 'Home / Landing Page',
      icon: 'home',
      route: '/',
      description: 'The landing page serves as the entry point to the AI Photo QC application. It provides an overview of the system and quick navigation to key features.',
      features: [
        'Welcome message and application overview',
        'Quick access to main features',
        'Navigation to Analysis Report and other sections'
      ]
    },
    {
      id: 'analysis-report',
      title: 'Analysis Report',
      icon: 'chart',
      route: '/analysis-report',
      description: 'The Analysis Report page displays a list of all survey templates that have been analyzed by the AI system. Each template shows a summary of questions and their overall confidence scores.',
      features: [
        'View all survey templates with AI analysis',
        'See question count per template',
        'View average confidence score for each template',
        'Search and filter templates by name',
        'Sort by template name, question count, or confidence score',
        'Click on any template to view detailed questions'
      ]
    },
    {
      id: 'analysis-report-questions',
      title: 'Analysis Report - Questions',
      icon: 'questions',
      route: '/analysis-report-questions',
      description: 'This page shows all questions within a selected survey template. Each question displays its AI-generated confidence score and associated prompts that guide the analysis.',
      features: [
        'View all questions for the selected template',
        'See confidence score for each question (displayed as donut chart)',
        'View prompts associated with each question',
        'Search questions by name, ID, or prompt text',
        'Sort by question ID, name, or confidence score',
        'Click on any question to view location-level details'
      ]
    },
    {
      id: 'analysis-report-details',
      title: 'Analysis Report - Question Details',
      icon: 'details',
      route: '/analysis-report-questions-details',
      description: 'The Question Details page provides a granular view of how each location responded to a specific question. It shows quality scores, AI analysis, and allows you to view the actual images submitted.',
      features: [
        'View total locations that answered the question',
        'See aggregate quality score as percentage',
        'Location-by-location breakdown with:',
        '  - Quality standard pass/fail status',
        '  - Score (0-3 scale with donut chart visualization)',
        '  - AI-generated analysis text',
        '  - Link to view submitted image',
        'Search by location or analysis text',
        'Sort by any column',
        'Breadcrumb navigation for easy back-navigation'
      ]
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: 'dashboard',
      route: '/dashboard',
      description: 'The Dashboard provides an alternative view for analyzing survey data with different visualization options and metrics.',
      features: [
        'Overview of survey performance',
        'Visual charts and metrics',
        'Quick access to detailed analysis'
      ]
    }
  ];

  constructor() {}

  ngOnInit() {
    this.initializeTheme();
  }

  ngAfterViewInit() {}

  initializeTheme() {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    if (savedTheme) {
      this.currentTheme = savedTheme;
      this.applyTheme(savedTheme);
    }
  }

  setTheme(theme: 'light' | 'dark') {
    this.currentTheme = theme;
    localStorage.setItem('theme', theme);
    this.applyTheme(theme);
  }

  applyTheme(theme: 'light' | 'dark') {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
